// Date-based conference grouping for the event rail. The batch package carries
// no dayjs dependency (see relativeTime.ts) — conference dates are `YYYY-MM-DD`,
// which compare correctly as plain strings, so grouping is pure string math.

import type { BatchConference } from './types';
import type { EventGroup } from './EventCard';

/** Today as `YYYY-MM-DD` in local time. */
export function todayISO(): string {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}

/**
 * Classify a conference relative to today. Undated events can't be classified,
 * so they sink to "past" (they still render; default selection falls back to
 * the most-recent overall). Mirrors the portal's `groupOf` in EventRail.
 */
export function groupOf(c: BatchConference, today: string = todayISO()): EventGroup {
  const { startDate, endDate } = c;
  if (!startDate || !endDate) return 'past';
  if (today < startDate) return 'upcoming';
  if (today > endDate) return 'past';
  return 'active';
}

/**
 * Default rail selection: the most-recent ACTIVE event (active group sorted by
 * startDate desc), falling back to the most-recent event overall when nothing
 * is active. Returns null for an empty list.
 */
export function defaultConferenceId(conferences: BatchConference[]): string | null {
  if (conferences.length === 0) return null;
  const today = todayISO();
  const active = conferences.filter((c) => groupOf(c, today) === 'active');
  const pool = active.length > 0 ? active : conferences;
  const sorted = [...pool].sort((a, b) =>
    (b.startDate ?? '').localeCompare(a.startDate ?? ''),
  );
  return sorted[0]?.id ?? conferences[0].id;
}
